import { defineComponent, ExtractPropTypes } from 'vue';
import {
  createNamespaceBem,
  makeCustomProp,
  makeStringProp,
  makeArrayProp,
  makeNumberProp,
  makeNumericProp,
  makeBoolProp,
} from '@/utils';
import { Button, Icon, Image } from 'vant';
import { TSignStatusValues, TDoctorInfoInGroupList } from '@tencent/fd-types/patient';
import { SignStatusEnum as CardStatusEnum } from '@tencent/fd-types';
import { t } from '@/assets/theme';
import doctorAvatar from '@/assets/images/doctor.png';

const { name, bem } = createNamespaceBem('sign-card');

export type CardStatusType = keyof typeof CardStatusEnum;

const signCardProps = {
  data: makeCustomProp<any>(Object, null),
  status: makeNumberProp<TSignStatusValues>(0),
  avatar: makeStringProp(''),
  name: makeStringProp(''),
  relation: makeStringProp(''),
  gender: makeStringProp(''),
  age: makeNumericProp(''),
  headerActionText: makeStringProp('查看'),
  doctorGroupTitle: makeStringProp(''),
  bodyList: makeArrayProp<TDoctorInfoInGroupList>(),
  expireDate: makeStringProp(''),
  type: makeStringProp(''),
  signOnline: makeBoolProp(false),
};

export type SignCardProps = ExtractPropTypes<typeof signCardProps>;

export default defineComponent({
  name,
  props: signCardProps,
  emits: [
    'clickHeaderLink',
    'clickSignBtn',
    'clickSignDetail',
    'clickSignCheck',
    'clickSignRenewal',
    'clickSignChange',
  ],
  setup(props, { emit }) {
    const onClickHeaderLink = () => {
      emit('clickHeaderLink', props.data);
    };
    const onClickSign = () => {
      emit('clickSignBtn', props.data);
    };
    const onClickDetail = () => {
      emit('clickSignDetail', props.data);
    };
    const onClickCheck = () => {
      emit('clickSignCheck', props.data);
    };
    const signUpRenewal = () => {
      emit('clickSignRenewal', props.data);
    };
    const onClickChange = () => {
      emit('clickSignChange', props.data);
    };

    const renderAvatar = () => {
      return (
        <div class={bem('avatar')}>
          <img src={props.avatar} alt="avatar" />
        </div>
      );
    };

    const renderInfo = () => {
      return (
        <div class={bem('info')}>
          <div class="name">
            <span>{props.name}</span>
            <span class="tag">{props.relation}</span>
          </div>
          <div class="gender">
            <span>{props.gender}</span>
            <span class="point"></span>
            {Number(props.age) > -1 && <span class="age">{Number(props.age)}岁</span>}
          </div>
        </div>
      );
    };

    const renderHeader = () => (
      <div class={bem('header')}>
        <div class={bem('wrap')}>
          {renderAvatar()}
          {renderInfo()}
        </div>
        {props.type === 'sign' && (
          <div class={bem('header-right')} onClick={onClickHeaderLink}>
            {props.headerActionText}
            <Icon name="arrow" />
          </div>
        )}
      </div>
    );

    const renderBodyList = () => (
      <>
        <div class={bem('body-header')}>{props.doctorGroupTitle} </div>
        <div class={bem('body-list')}>
          {props.bodyList.map((i) => (
            <div class={bem('body-list-item')}>
              <Image round lazyLoad src={i.thumbAvatar ? i.thumbAvatar : doctorAvatar} iconSize={t(14)} />
              <span>{i.name}</span>
            </div>
          ))}
        </div>
      </>
    );

    const renderFooter = () => (
      <div class={bem('footer')}>
        {props.status === CardStatusEnum.protect && <div class={bem('footer-left')}>服务到期 {props.expireDate}</div>}
        {props.status === CardStatusEnum.checking && <div class={bem('footer-left')}>签约资料已提交，审核中</div>}
        {props.status === CardStatusEnum.modifying && <div class={bem('footer-left')}>变更申请已提交，审核中</div>}
        {props.status === CardStatusEnum.expiring && (
          <div class={bem('footer-left', [CardStatusEnum[props.status]])}>
            家庭医生服务<span>即将到期</span>
          </div>
        )}
        {props.status === CardStatusEnum.expired && (
          <div class={bem('footer-left', [CardStatusEnum[props.status]])}>
            家庭医生服务<span>已到期</span>
          </div>
        )}
        {/* 支持在线签约才有续约入口 */}
        {props.type === 'sign' && props.signOnline && props.status === CardStatusEnum.expired && (
          <div class={bem('footer-right')} onClick={signUpRenewal}>
            原方案续约
            <Icon name="arrow" />
          </div>
        )}
        {props.type === 'sign' &&
          ([CardStatusEnum.protect, CardStatusEnum.expiring].includes(props.status as any) ||
            (!props.signOnline && props.status === CardStatusEnum.expired)) && (
            <div class={bem('footer-right')} onClick={onClickDetail}>
              签约详情
              <Icon name="arrow" />
            </div>
          )}
        {props.type === 'sign' && props.status === CardStatusEnum.checking && (
          <div class={bem('footer-right')} onClick={onClickCheck}>
            修改
            <Icon name="arrow" />
          </div>
        )}
        {/* 支持在线签约才有变更入口 */}
        {props.type === 'modify' && props.signOnline && props.status !== CardStatusEnum.modifying && (
          <div class={bem('footer-right')} onClick={onClickChange}>
            变更签约
            <Icon name="arrow" />
          </div>
        )}
        {props.status === CardStatusEnum.modifying && (
          <div class={bem('footer-right')} onClick={onClickChange}>
            查看
            <Icon name="arrow" />
          </div>
        )}
      </div>
    );

    const renderBody = () => (
      <div class={bem('body', [CardStatusEnum[props.status]])}>
        {/* 未签约 */}
        {props.status === CardStatusEnum.unsign && (
          <>
            <div class={bem('msg')}>暂无家庭医生</div>
            {props.type === 'sign' && props.signOnline && (
              <Button type="primary" size="small" onClick={onClickSign}>
                立即签约
              </Button>
            )}
            {props.type === 'modify' && (
              <div class={bem('footer-right')} onClick={onClickChange}>
                变更签约
                <Icon name="arrow" />
              </div>
            )}
          </>
        )}
        {props.status === CardStatusEnum.reject && (
          <>
            <div class={bem('msg')}>申请已驳回，请修改后重新提交</div>
            <Button type="primary" size="small" onClick={onClickCheck}>
              修改
            </Button>
          </>
        )}
        {/* 医生列表 */}
        {[
          CardStatusEnum.protect,
          CardStatusEnum.expiring,
          CardStatusEnum.expired,
          CardStatusEnum.checking,
          CardStatusEnum.modifying,
        ].includes(props.status as any) && renderBodyList()}
        {/* 底部 */}
        {[
          CardStatusEnum.protect,
          CardStatusEnum.expiring,
          CardStatusEnum.checking,
          CardStatusEnum.expired,
          CardStatusEnum.modifying,
        ].includes(props.status as any) && renderFooter()}
        {/* 右上角标签 */}
        {props.status === CardStatusEnum.protect && (
          <div class={bem('tag', [CardStatusEnum[props.status]])}>保障中</div>
        )}
        {props.status === CardStatusEnum.expiring && (
          <div class={bem('tag', [CardStatusEnum[props.status]])}>即将到期</div>
        )}
        {props.status === CardStatusEnum.checking && (
          <div class={bem('tag', [CardStatusEnum[props.status]])}>审核中</div>
        )}
        {props.status === CardStatusEnum.expired && (
          <div class={bem('tag', [CardStatusEnum[props.status]])}>已过期</div>
        )}
        {props.status === CardStatusEnum.modifying && (
          <div class={bem('tag', [CardStatusEnum[props.status]])}>变更审核中</div>
        )}
      </div>
    );

    return () => (
      <div class={bem()}>
        {renderHeader()}
        {renderBody()}
      </div>
    );
  },
});
